import { useState, useCallback } from "react";

/**
 * Custom hook to manage a Set of IDs for tracking async operations
 * Useful for tracking loading states for multiple items (e.g., deleting, processing)
 *
 * Usage:
 *   const [deletingIds, deletingOps] = useAsyncSet();
 *   deletingOps.add(id);
 *   if (deletingOps.has(id)) { ... }
 *   deletingOps.remove(id);
 *
 * Note: Use deletingOps.has(id) instead of deletingIds.has(id) to ensure
 * proper memoization and avoid unnecessary re-renders.
 */
const useAsyncSet = (initialValues = []) => {
  const [set, setSet] = useState(new Set(initialValues));

  const add = useCallback((value) => {
    setSet((prev) => new Set(prev).add(value));
  }, []);

  const remove = useCallback((value) => {
    setSet((prev) => {
      const next = new Set(prev);
      next.delete(value);
      return next;
    });
  }, []);

  const has = useCallback(
    (value) => {
      return set.has(value);
    },
    [set],
  );

  const clear = useCallback(() => {
    setSet(new Set());
  }, []);

  const size = set.size;

  // Return the Set for compatibility, but operations object is the correct API
  return [set, { add, remove, has, clear, size }];
};

export default useAsyncSet;
